import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Add, Bundle, Product } from "@dreamhub/designsystem/icons";
import { Button } from "@/components/ui/button";
import IconContainer from "@/components/ui/icon-container";
import OptionsDropdownMenu, {
  DetailedViewOptions,
  OptionsDropdownSection
} from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import SearchInput from "@/components/ui/search-input";
import Spinner from "@/components/ui/spinner";
import Text from "@/components/ui/text";
import Chip from "@/components/ui/chip";
import {
  ProductBundle,
  ServiceItemType
} from "@/pages/settings/pages/products-v2/types";
import { BundleMenuList } from "./BundleMenuList";

const SEARCH_THRESHOLD = 10;

interface AddServiceDropdownProps {
  serviceOptions: DetailedViewOptions;
  sections: OptionsDropdownSection[];
  variant?: "primary" | "tertiary";
  emptyStateContent?: React.ReactNode;
  disabled?: boolean;
  disabledTooltipText?: string;
  // Locks the trigger behind a spinner while a pick is still being handled.
  isLoading?: boolean;
  // Lets the caller stretch the trigger over its whole row.
  triggerClassName?: string;
  // With bundles to offer, the menu gets Services and Bundles chips.
  bundles?: ProductBundle[];
  serviceItemTypes?: ServiceItemType[];
  onSelectBundle?: (bundle: ProductBundle) => void;
}

type MenuTab = "services" | "bundles";

interface ServiceEntry {
  key: string;
  label: string;
  secondaryText?: string;
  sectionId: string;
  action: () => void;
  disabled?: boolean;
}

const AddServiceDropdown: React.FC<AddServiceDropdownProps> = ({
  serviceOptions,
  sections,
  variant = "primary",
  emptyStateContent,
  disabled = false,
  disabledTooltipText,
  isLoading = false,
  triggerClassName,
  bundles = [],
  serviceItemTypes = [],
  onSelectBundle
}) => {
  const { t } = useTranslation();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [tab, setTab] = useState<MenuTab>("services");
  const locked = disabled || isLoading;
  const showBundles = !!onSelectBundle && bundles.length > 0;

  useEffect(() => {
    // Reset popover state on lock so a later re-enable doesn't reopen it
    // with stale search text.
    if (locked) {
      setIsPopoverOpen(false);
      setSearchValue("");
      setTab("services");
    }
  }, [locked]);

  const optionEntries = useMemo<ServiceEntry[]>(() => {
    const entries: ServiceEntry[] = [];
    for (const [key, value] of Object.entries(serviceOptions)) {
      if (typeof value !== "object" || !("action" in value)) continue;
      entries.push({
        key,
        label: value.customText ?? key,
        secondaryText: value.secondaryText,
        sectionId: value.section ?? "default",
        action: value.action,
        disabled: value.disabled
      });
    }
    return entries;
  }, [serviceOptions]);

  const hasNoOptions = optionEntries.length === 0;
  const needsSearch = optionEntries.length > SEARCH_THRESHOLD;

  const filteredEntries = useMemo(() => {
    const normalizedSearchValue = searchValue.trim().toLowerCase();
    if (!normalizedSearchValue) return optionEntries;
    return optionEntries.filter((entry) =>
      entry.label.toLowerCase().includes(normalizedSearchValue)
    );
  }, [optionEntries, searchValue]);

  const groupedFiltered = useMemo(() => {
    const groups: Record<string, ServiceEntry[]> = {};
    for (const entry of filteredEntries) {
      (groups[entry.sectionId] ??= []).push(entry);
    }
    return groups;
  }, [filteredEntries]);

  const orderedVisibleSections = useMemo(() => {
    return sections.filter((section) => groupedFiltered[section.id]?.length);
  }, [sections, groupedFiltered]);

  const handleSearchablePopoverChange = (open: boolean) => {
    setIsPopoverOpen(open);
    if (!open) {
      setSearchValue("");
      setTab("services");
    }
  };

  const closeMenu = () => handleSearchablePopoverChange(false);

  const serviceList =
    filteredEntries.length === 0 ? (
      <Text variant="bodyTertiary" className="px-2 py-4 text-center">
        {t("tables.emptyState.title")}
      </Text>
    ) : (
      orderedVisibleSections.map((section) => (
        <div key={section.id} className="flex flex-col">
          <div className="px-2 pt-2 pb-[2px]">
            <Text variant="smBodySecondary">{section.title}</Text>
          </div>
          {groupedFiltered[section.id].map((entry) => (
            <button
              key={entry.key}
              type="button"
              data-test={entry.key}
              disabled={entry.disabled}
              onClick={() => {
                entry.action();
                closeMenu();
              }}
              className="flex items-center justify-between gap-4 px-2 py-1.5 rounded-sm text-left hover:bg-surface-hover-soft active:bg-surface-pressed disabled:opacity-50 disabled:pointer-events-none"
            >
              <Text variant="body" className="flex-1 truncate">
                {entry.label}
              </Text>
              {entry.secondaryText ? (
                <Text variant="smBodyTertiary">{entry.secondaryText}</Text>
              ) : null}
            </button>
          ))}
        </div>
      ))
    );

  const chip = (value: MenuTab, icon: React.ReactNode, label: string) => {
    const isSelected = tab === value;
    const select = () => {
      setTab(value);
      setSearchValue("");
    };
    return (
      <Chip
        size="md"
        label={label}
        selected={isSelected}
        icon={
          <IconContainer
            as="span"
            size="sm"
            color={isSelected ? "primary" : "tertiary"}
            aria-hidden="true"
          >
            {icon}
          </IconContainer>
        }
        onClick={select}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            select();
          }
        }}
        role="button"
        tabIndex={0}
        aria-pressed={isSelected}
        data-test={`add-service-chip-${value}`}
      />
    );
  };

  const triggerButton = (
    <Button
      variant={variant}
      className={triggerClassName}
      disabled={locked}
      tooltipText={disabled ? disabledTooltipText : undefined}
      aria-busy={isLoading}
    >
      {isLoading ? (
        <Spinner size="sm" />
      ) : (
        <IconContainer
          size="sm"
          color={
            disabled ? "disabled" : variant === "primary" ? "on-primary" : "cta"
          }
        >
          <Add />
        </IconContainer>
      )}
      {t("contractTerms.addService")}
    </Button>
  );

  if (locked) {
    return triggerButton;
  }

  if (showBundles) {
    return (
      <Popover
        open={isPopoverOpen}
        onOpenChange={handleSearchablePopoverChange}
      >
        <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
        <PopoverContent
          align="start"
          padding={4}
          className="w-[360px]"
          data-test="add-service-menu"
        >
          <div className="flex max-h-[360px] flex-col gap-1">
            <div className="sticky top-0 z-10 bg-surface-elevation2 px-1 pt-1">
              <SearchInput
                placeholder={t(
                  tab === "bundles"
                    ? "contractTerms.bundle.searchBundles"
                    : "contractTerms.bundle.searchServices"
                )}
                value={searchValue}
                onChange={setSearchValue}
              />
            </div>
            <div className="flex items-center gap-1 p-1">
              {chip(
                "services",
                <Product />,
                t("contractTerms.bundle.servicesChip")
              )}
              {chip(
                "bundles",
                <Bundle />,
                t("contractTerms.bundle.bundlesChip")
              )}
            </div>
            <div className="flex flex-col overflow-y-auto">
              {tab === "bundles" ? (
                <BundleMenuList
                  bundles={bundles}
                  search={searchValue}
                  serviceItemTypes={serviceItemTypes}
                  onSelect={(bundle) => {
                    onSelectBundle?.(bundle);
                    closeMenu();
                  }}
                />
              ) : (
                serviceList
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  if (hasNoOptions && emptyStateContent) {
    return (
      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
        <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
        <PopoverContent padding={16} className="w-80">
          {emptyStateContent}
        </PopoverContent>
      </Popover>
    );
  }

  if (needsSearch) {
    return (
      <Popover
        open={isPopoverOpen}
        onOpenChange={handleSearchablePopoverChange}
      >
        <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
        <PopoverContent align="start" padding={4} className="min-w-[320px]">
          <div className="flex flex-col gap-1 max-h-[300px]">
            <div className="sticky top-0 bg-surface-elevation2 z-10 px-1 pt-1">
              <SearchInput
                placeholder={t("search")}
                value={searchValue}
                onChange={setSearchValue}
              />
            </div>
            <div className="flex flex-col overflow-y-auto">{serviceList}</div>
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <OptionsDropdownMenu
      options={serviceOptions}
      tPrefix="services"
      enableSections={true}
      sections={sections}
      showIcons={false}
      triggerIcon="add"
      contentClassName="min-w-[320px]"
      align="start"
      customTrigger={triggerButton}
    />
  );
};

export default AddServiceDropdown;
